import React from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import { useMaybePlayAudioOnceOnMount } from 'features/app-takeover/utils/play-audio-once';
import { ProductWizardStep as OneMenuProductWizardStep } from 'features/menu/components/product-wizard/types';
import { logButtonClick } from 'features/navigation/utils/analytics';
import Animated, {
  FadeIn,
  LayoutAnimationConfig,
  SlideInLeft,
  SlideInRight,
} from 'react-native-reanimated';

import { InnerLayout, LayoutContainer } from 'components/layout/styled';
import { PageAnnouncer } from 'components/page-announcer';
import { ProductCta } from 'components/product-detail-v2/product-cta';
import { ProductDetailHeader } from 'components/product-detail-v2/product-detail-flow/product-detail-header';
import {
  transformComboSlotTypeToEventAttr,
  transformProductWizardStepToEventAttr,
} from 'components/product-detail-v2/utils';
import { MenuObjectTypes } from 'enums/menu';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { ClickEventComponentNames } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { ProductWizardStep } from 'state/product-wizard-v2/types';
import { isPicker } from 'utils/menu';

import { ComboSlotType } from '../ComboSlotType';
import { ProductCustomizationOptions } from '../product-customization-options';

import { ProductCustomizationDrinksTab } from './product-customization-drinks-tab';
import { TabsContent } from './styled';
import { DrinkCategoriesKeys, DrinksCategories, IProductCustomizationDrinks } from './types';
import {
  categorizeDrinksOptions,
  getItemCategory,
  getValidTabKey,
  orderedCategories,
} from './utils';

const DRINKS_SLIDE_IN_ANIMATION_DURATION = 300;
const DRINKS_FADE_IN_ANIMATION_DURATION = 700;

export const ProductCustomizationDrinks: React.FC<IProductCustomizationDrinks> = ({
  slotKeyToCustomize,
  comboSlot,
  comboSlotOptions,
  currentStep,
  selectedItem,
  handleComboSlotSelectionChange,
  modifierSelections,
  defaultModifiersMap,
  handleChangeModifierSelections,
  pickerAspectExclusions,
  comboSlotType,
  onPressBackNavigation,
  backNavigationLabel = '',
}) => {
  const isMobile = useIsMobileBreakpoint();
  const isMountedRef = React.useRef<boolean>(false);
  const { menuObject, updateProductWizardFlowSteps } = useProductWizardContext();

  const { additionalAudioSteps } = useAppTakeoverPdp(menuObject?._id);
  useMaybePlayAudioOnceOnMount(
    additionalAudioSteps?.[OneMenuProductWizardStep.PickerCustomizationDrinks]
  );

  // Track the previous index of the active tab
  const previousTabIndexRef = React.useRef<number>(0);

  const [activeDrinksTab, setActiveDrinksTab] = React.useState<DrinkCategoriesKeys>(() => {
    return selectedItem ? getItemCategory(selectedItem) : DrinksCategories.SOFT_DRINKS;
  });

  const categorizedDrinksOptions = React.useMemo(() => categorizeDrinksOptions(comboSlotOptions), [
    comboSlotOptions,
  ]);

  // If a Drink Category has no available drinks, then that tab should be hidden from the user.
  const filteredCategories = React.useMemo(
    () =>
      orderedCategories.filter(
        category =>
          categorizedDrinksOptions[category] && categorizedDrinksOptions[category].length > 0
      ),
    [categorizedDrinksOptions]
  );

  const filteredDrinksOptions = React.useMemo(() => {
    return categorizedDrinksOptions[activeDrinksTab] || [];
  }, [activeDrinksTab, categorizedDrinksOptions]);

  const handleDrinksTabChange = (tab: DrinkCategoriesKeys) => {
    const newTabKey = DrinksCategories[getValidTabKey(tab) as DrinkCategoriesKeys];
    const currentTabIndex = filteredCategories.indexOf(newTabKey);

    if (currentTabIndex !== -1) {
      // Update the previous tab index ref before changing the state
      previousTabIndexRef.current = filteredCategories.indexOf(DrinksCategories[activeDrinksTab]);
      setActiveDrinksTab(newTabKey);

      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.PRODUCT_DRINKS_CATEGORY_VIEW,
          name: newTabKey,
        },
      });
    }
  };

  const enteringAnimation = React.useMemo(() => {
    if (!isMountedRef.current) {
      return;
    }

    const currentTabIndex = filteredCategories.indexOf(DrinksCategories[activeDrinksTab]);
    const previousTabIndex = previousTabIndexRef.current;

    // Determine if we are moving forward or backward
    if (currentTabIndex > previousTabIndex) {
      return SlideInRight;
    } else if (currentTabIndex < previousTabIndex) {
      return SlideInLeft;
    }

    // Default animation if we are at the same tab (no change)
    return SlideInRight;
  }, [activeDrinksTab, filteredCategories]);

  // LayoutAnimationConfig skipEntering logic for web devices
  React.useLayoutEffect(() => {
    isMountedRef.current = true;
  }, []);

  React.useEffect(() => {
    updateProductWizardFlowSteps({
      id: slotKeyToCustomize,
      type: MenuObjectTypes.COMBO_SLOT,
      currentStep: true,
      comboSlotType: ComboSlotType.DRINKS,
    });
  }, [slotKeyToCustomize, updateProductWizardFlowSteps]);

  if ((!isMobile && !isMountedRef.current) || !selectedItem) {
    return null;
  }

  const shouldDisplaySelectedItemNameTitle =
    !isPicker(menuObject) || currentStep === ProductWizardStep.ProductEditOrder;

  const productDetailHeaderTitle =
    shouldDisplaySelectedItemNameTitle && selectedItem
      ? selectedItem.name.locale
      : t('chooseDrink');

  return (
    <>
      <ProductDetailHeader
        title={productDetailHeaderTitle}
        currentStep={ProductWizardStep.ProductCustomization}
        comboSlotType={comboSlotType}
        onPress={onPressBackNavigation}
        backNavigationLabel={backNavigationLabel}
        eventAttributes={{
          name: transformProductWizardStepToEventAttr(ProductWizardStep.ProductCustomization),
          comboSlotType: transformComboSlotTypeToEventAttr(ComboSlotType.DRINKS),
        }}
      />

      <ScrollView showsVerticalScrollIndicator>
        <LayoutContainer isFullContainer={isMobile}>
          <InnerLayout>
            <ProductCustomizationDrinksTab
              activeTab={activeDrinksTab}
              categories={filteredCategories}
              onTabChange={handleDrinksTabChange}
            />
            <TabsContent>
              <LayoutAnimationConfig skipEntering>
                <Animated.View
                  key={activeDrinksTab}
                  entering={FadeIn.duration(DRINKS_FADE_IN_ANIMATION_DURATION)}
                >
                  <Animated.View
                    entering={enteringAnimation?.duration(DRINKS_SLIDE_IN_ANIMATION_DURATION)}
                  >
                    <ProductCustomizationOptions
                      slotKeyToCustomize={slotKeyToCustomize}
                      comboSlot={comboSlot}
                      comboSlotOptions={filteredDrinksOptions}
                      selectedItem={selectedItem}
                      onSelectComboSlot={handleComboSlotSelectionChange}
                      comboSlotType={ComboSlotType.SIDES}
                      modifierSelections={modifierSelections}
                      modifierDefaults={defaultModifiersMap}
                      onModifierSelectionsChange={handleChangeModifierSelections}
                      pickerAspectExclusions={pickerAspectExclusions}
                    />
                  </Animated.View>
                </Animated.View>
              </LayoutAnimationConfig>
            </TabsContent>
          </InnerLayout>
        </LayoutContainer>
      </ScrollView>

      <ProductCta
        currentStep={currentStep}
        menuObject={menuObject}
        modifierSelections={modifierSelections}
        selectedItem={selectedItem}
      />

      <PageAnnouncer pageTitle={productDetailHeaderTitle} />
    </>
  );
};
